<template>
    <v-window v-model="step">
        <v-window-item value="default">
            <v-row no-gutters class="my-2">
                <v-col class="d-flex align-center flex-shrink-0 flex-grow-1">
                    <v-select-filled
                        v-model="paymentType"
                        :items="paymentTypes"
                        item-title="text"
                        item-value="value"
                        outlined
                        hide-details
                        label="Type de paiement"
                        density="compact"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="my-2">
                <v-col class="d-flex align-center flex-shrink-0 flex-grow-1">
                    <v-text-field-filled
                        v-model="search"
                        label="Rechercher"
                        outlined
                        hide-details
                        density="compact"
                        clearable
                        append-inner-icon="$search"
                        @click:clear="search = ''"
                    />
                </v-col>
                <v-col class="d-flex align-center justify-end flex-shrink-1 flex-grow-0 pl-2">
                    <v-btn
                        @click="
                            step = 'createIban';
                            emitStepFromComponent();
                        "
                        rounded="xl"
                        elevation="0"
                        color="primary primary"
                        dark
                        size="small"
                    >
                        Ajouter <v-icon right>$add</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <template
                two-line
                subheader
                style="height: 40vh; overflow: scroll"
                v-if="!loading && thirdPartyCompanyData"
            >
                <template v-if="!thirdPartyCompanyIbans.length && !search">
                    Aucun IBAN ajouté
                </template>
                <template v-else-if="!thirdPartyCompanyIbans.length">
                    Aucun résultat pour cet IBAN
                </template>
                <template v-else>
                    <v-list>
                        <v-list-item
                            v-for="(bankAccount, i) in thirdPartyCompanyIbans"
                            :key="i"
                            class="mt-2"
                            style="background: #fff"
                            lines="two"
                            border
                            rounded="xl"
                        >
                            <v-list-item-title>
                                {{
                                    (bankAccount.name ? bankAccount.name + " - " : "") +
                                    bankAccount.iban +
                                    (bankAccount.bic ? " - " + bankAccount.bic : "")
                                }}
                            </v-list-item-title>
                            <template #append class="my-0">
                                <div class="d-flex">
                                    <v-btn-icon
                                        icon="$oss-edit"
                                        color="secondary"
                                        class="mx-1"
                                        v-model="bankAccount.iban"
                                        @click="editBankAccount(bankAccount)"
                                        size="small"
                                    />
                                    <v-btn-trash
                                        class="mx-1"
                                        v-model="bankAccount.iban"
                                        @click.stop="openDeleteIbanDialog(bankAccount)"
                                        size="small"
                                    />
                                </div>
                            </template>
                        </v-list-item>
                    </v-list>
                    <oss-dialog
                        v-if="bankAccountToDelete"
                        v-model="deleteDialog"
                        :title="`Supprimer l'IBAN ${bankAccountToDelete.iban}`"
                        @valid="deleteIban"
                        error
                        width="800px"
                    >
                    </oss-dialog>
                    <v-row v-if="!loading && !thirdPartyCompanyIbans.length">
                        <v-col cols="12" class="d-flex align-center justify-center">
                            Vous n'avez aucun iban enregistré
                        </v-col>
                    </v-row>
                </template>
            </template>
            <v-row v-else>
                <v-col cols="12" class="text-center">
                    <v-progress-circular indeterminate color="primary" />
                </v-col>
            </v-row>
        </v-window-item>
        <v-window-item value="createIban">
            <v-form v-model="isValidIbanForm">
                <v-row no-gutter class="mt-2">
                    <v-col cols="6" class="pr-1">
                        <v-text-field-filled
                            v-model="newBankAccount.iban"
                            label="IBAN"
                            outlined
                            :rules="[rules.notEmpty, rules.checkIBAN, rules.ibanAlreadyExists]"
                            @change="onIbanChanged(newBankAccount)"
                            @input="newBankAccount.iban = truncateInput(newBankAccount.iban, 34)"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-text-field-filled
                            v-model="newBankAccount.bic"
                            label="BIC"
                            outlined
                            :rules="[rules.notEmpty, rules.checkBIC]"
                            density="compact"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutter>
                    <v-col cols="12">
                        <v-text-field-filled
                            v-model="newBankAccount.name"
                            label="Nom de la banque"
                            outlined
                            :rules="[rules.notEmpty]"
                            density="compact"
                        />
                    </v-col>
                </v-row>
                <v-divider />
                <v-row no-gutter class="mt-2" v-if="activateMandate">
                    <v-col cols="6" class="pr-1">
                        <v-text-field-filled
                            v-model="newBankAccount.mandate.id"
                            label="RUM"
                            outlined
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="6">
                        <oss-date-picker-filled
                            v-model="newBankAccount.mandate.date"
                            hide-details
                            :textFieldReadOnly="false"
                            label="Date de signature"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-window-item>
    </v-window>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import THIRD_COMPANY_DATA from "../graphql/invoice/ThirdPartyCompanyData.gql";
import UPSERT_THIRD_COMPANY_DATA from "../graphql/company/UpsertThirdPartyCompany.gql";
import {
    checkBIC,
    checkIBAN,
    getBankNameFromIban,
    getBICFromIBAN,
    onIbanChanged,
} from "../../../backend/src/common/bankAccount/bankAccountServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    emits: ["emit-upsert", "step", "edit-mode", "iban-rules"],
})
export class ThirdCompanyIbansEdition extends Vue {
    // @Prop({ required: true }) c!:string;
    @Prop({ required: true }) companyId!: string;
    @Prop({ required: true }) inThirdPartyCompanyId!: string;
    @Prop({ required: true }) thirdPartyCompanyType!: string;
    @Prop({ default: null }) actions: any;

    newBankAccount = {
        name: "",
        iban: "",
        bic: "",
        default: true,
        mandate: {
            id: "",
            date: "",
        },
    };

    paymentType: string = null;

    paymentTypes = [
        { value: null, text: "Aucun" },
        { value: "TRANSFER", text: "Paiement par virement" },
        { value: "DIRECT_DEBIT", text: "Paiement par prélèvement" },
        { value: "CHEQUE", text: "Paiement par chèque" },
    ];

    step = "default";

    search = "";

    isValidIbanForm = false;

    ibanExists = false;

    previousIban = "";

    loading = false;

    thirdPartyCompanyId_: string = "";

    deleteDialog: boolean = false;
    bankAccountToDelete: any = null;

    get thirdPartyCompanyId() {
        if (this.thirdPartyCompanyId_) {
            return this.thirdPartyCompanyId_;
        }
        return this.inThirdPartyCompanyId;
    }

    set thirdPartyCompanyId(value) {
        this.thirdPartyCompanyId_ = value;
    }

    @Watch("paymentType")
    async onPaymentTypeChanged() {
        this.upsertPaymentType();
    }

    @Apollo({
        query: THIRD_COMPANY_DATA,
        variables() {
            return {
                // company: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
            };
        },
        skip() {
            return !this.companyId || !this.thirdPartyCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loading = loading;
            if (!loading) {
                debugger;
                this.paymentType = data?.company?.thirdPartyCompanyPaymentType || null;
                if (data?.company?.thirdPartyCompanyData) {
                    // debugger;
                    let thirdPartyCompanyData = data.company.thirdPartyCompanyData;

                    if (thirdPartyCompanyData.constructor.name == "Array") {
                        thirdPartyCompanyData = {
                            ibans: thirdPartyCompanyData,
                        };
                    }
                    if (!thirdPartyCompanyData.ibans) {
                        thirdPartyCompanyData.ibans = [];
                        this.cleanNewBankAccount();
                    } else {
                        for (const bankAccount of thirdPartyCompanyData.ibans) {
                            if (!bankAccount.bic) {
                                if (bankAccount.iban) {
                                    bankAccount.bic = getBICFromIBAN(bankAccount.iban);
                                } else {
                                    bankAccount.bic = "";
                                }
                            }
                            if (!bankAccount.name) {
                                bankAccount.name = getBankNameFromIban(bankAccount.name);
                            }
                            if (this.activateMandate) {
                                if (!bankAccount.mandate) {
                                    bankAccount.mandate = {
                                        id: "",
                                        date: "",
                                    };
                                }
                            }
                        }
                    }
                    this.thirdPartyCompanyData = thirdPartyCompanyData;
                } else {
                    this.thirdPartyCompanyData = { ibans: [] };
                    this.cleanNewBankAccount();
                }
            }
        },
    })
    thirdPartyCompanyData: any = {
        ibans: [],
    };

    beforeMount() {
        if (this.actions && this.actions.action) {
            this.actions.action.function = this.upsertIban;
            this.actions.action.title = () => {
                return !(this.previousIban === "") ? "Modifier l'IBAN" : "Ajouter un IBAN";
            };
            this.actions.action.disabled = () => {
                return !this.isValidIbanForm;
            };
            this.actions.action.display = () => {
                return this.step === "createIban";
            };
        }
    }

    async upsertPaymentType() {
        const result: any = await this.$apollo.mutate({
            mutation: UPSERT_THIRD_COMPANY_DATA,
            variables: {
                parentCompanyId: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                paymentType: this.paymentType,
            },
        });
    }

    async upsertIban() {
        // const ibanCreate = JSON.parse(JSON.stringify(this.newBankAccount));

        if (this.thirdPartyCompanyData) {
            if (this.thirdPartyCompanyData.ibans) {
                if (!this.ibanExists) {
                    this.thirdPartyCompanyData.ibans.push(this.newBankAccount);
                } else {
                    debugger;
                    const indexReplaceIban = this.thirdPartyCompanyData.ibans.findIndex(
                        (bankAccount) => bankAccount.iban === this.previousIban,
                    );
                    this.thirdPartyCompanyData.ibans[indexReplaceIban] = this.newBankAccount;
                }
            } else {
                this.thirdPartyCompanyData.ibans = [this.newBankAccount];
            }
        }
        // this.thirdPartyCompanyData.ibans = [ibanCreate]
        // console.log("data", this.thirdPartyCompanyData)
        const result: any = await this.$apollo.mutate({
            mutation: UPSERT_THIRD_COMPANY_DATA,
            variables: {
                parentCompanyId: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                data: this.thirdPartyCompanyData,
            },
        });

        if (result?.data?.upsertThirdPartyCompany) {
            this.thirdPartyCompanyId = result.data.upsertThirdPartyCompany.id;
            this.$emit("emit-upsert", true);
        }

        this.cleanNewBankAccount();

        // this.thirdPartyCompanyDatasID = thirdPartyCompanyDatas.id
        this.step = "default";

        // this.$apollo.queries.thirdPartyCompanyDatas.refetch()
    }

    get thirdPartyCompanyIbans() {
        debugger;
        if (this.thirdPartyCompanyData.ibans) {
            if (this.search) {
                return this.thirdPartyCompanyData.ibans.filter((bankAccount) =>
                    bankAccount.iban.toLowerCase().includes(this.search.toLowerCase()),
                );
            }
            return this.thirdPartyCompanyData.ibans;
        }
        return [];
    }

    get activateMandate() {
        return this.thirdPartyCompanyType === "customer";
    }

    emitStepFromComponent() {
        debugger;
        this.$emit("step", this.step);
    }

    emitEditMode() {
        this.$emit("edit-mode", true);
    }

    editBankAccount(bankAccount) {
        this.step = "createIban";
        this.emitStepFromComponent;
        this.emitEditMode;
        this.previousIban = bankAccount.iban;
        this.newBankAccount = JSON.parse(JSON.stringify(bankAccount));
        this.ibanExists = true;
    }

    changeStep() {
        this.previousIban = "";
        this.step = "default";
        this.$emit("step", this.step);
    }

    @Watch("isValidIbanForm")
    newIbanValidChange() {
        debugger;
        this.$emit("iban-rules", this.isValidIbanForm);
    }

    cleanNewBankAccount() {
        this.newBankAccount = {
            name: "",
            iban: "",
            bic: "",
            default: true,
            mandate: {
                id: "",
                date: "",
            },
        };
    }

    async deleteIban() {
        if (this.bankAccountToDelete?.iban) {
            const indexDeleteIban = this.thirdPartyCompanyData.ibans.findIndex(
                (bankAccount) => bankAccount.iban === this.bankAccountToDelete.iban,
            );
            if (indexDeleteIban !== -1) {
                this.thirdPartyCompanyData.ibans.splice(indexDeleteIban, 1);
                await this.$apollo.mutate({
                    mutation: UPSERT_THIRD_COMPANY_DATA,
                    variables: {
                        parentCompanyId: this.companyId,
                        thirdPartyCompanyId: this.thirdPartyCompanyId,
                        data: this.thirdPartyCompanyData,
                    },
                });
            } else {
                debugger;
            }
        }
        this.deleteDialog = false;
    }

    get rules() {
        return {
            notEmpty: (value) => {
                return !!value || this.$t("settings.itemRequired");
            },
            checkIBAN: (value) => {
                return checkIBAN(value) || this.$t("settings.wrongIBAN");
            },
            checkBIC: (value) => {
                return checkBIC(value) || this.$t("settings.wrongBIC");
            },
            ibanAlreadyExists: (value) => {
                for (const bankAccount of this.thirdPartyCompanyIbans) {
                    if (value === bankAccount.iban && !this.previousIban) {
                        return "L'IBAN existe déjà";
                    }
                }
                return true;
            },
        };
    }

    onIbanChanged(bankAccount) {
        onIbanChanged(bankAccount);
    }

    openDeleteIbanDialog(bankAccount) {
        this.bankAccountToDelete = bankAccount;
        this.deleteDialog = true;
    }
}

export default toNative(ThirdCompanyIbansEdition);
</script>
